import React, { useEffect, useState } from 'react';
import { withI18N } from "../hoc-components";
import compose from "../../utils/helper-functions/compose";
import Loader from "../helper-components/loader/loader";
import { config } from "../../config/config";
import { getCustomerData, setLimitSubmit, updateAdditionalFields, updateGatewayDetails } from "../../state/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { createFatalError } from "../../state/actions/errors-actions";
import { sendOpenSearchLogs } from "utils/helper-functions/send-open-search-logs.ts";
import { RecoverySettingsModel } from "utils/models/RecoverySettings.Model.ts";
import { fetchGateways, createCurrentGateway } from "state/actions";
import { ResponseStatus } from "../../variables/response-status";
import { isPanoramicTheme } from "@pano-project/variables/is-panoramic-2-0-theme";
import { withTemplate } from "../hoc-components/hoc-context/with-template";
import { isMobile } from "utils/detect-devices/is-mobile";
import { cashierLoaded } from "utils/custom-cashier-events/cashier-load-event";
import { useHistory } from 'react-router-dom';

const ProcessingCustomerData = (props) => {
    const history = useHistory();
    const { children, langBack, getCustomStyles, template: { sliderMethods }, theme } = props;
    const [customCssLoaded, setCustomCssLoaded] = useState(false);
    const location = useSelector(({ location }) => location, shallowEqual);
    const store = useSelector(({ gatewaysList, profileInfo: { transaction_type, base_amount, base_currency, customer, order_id }, currentGateway, location }) => (
        { gatewaysList, transaction_type, currentGateway, location, base_amount, base_currency, customer, order_id }
    ), shallowEqual);
    const { gatewaysList, currentGateway, transaction_type, base_amount, base_currency, customer, order_id } = store;
    const dispatch = useDispatch();

    const [loader, setLoader] = useState(true);

    const customCss = getCustomStyles();

    const needForAutoCreate = (gateways = []) => {
        if (currentGateway) return false;
        return gateways?.length === 1 || isPanoramicTheme || (sliderMethods !== "separate" && !isMobile);
    }

    const createGateway = (gateway) => {
        dispatch(createCurrentGateway(gateway)).then(() => {
            history.push("payment-method");
        })
    };

    useEffect(() => {
        if (!location || !transaction_type || !isPanoramicTheme || defineIsCallback()) return;

        if (!gatewaysList) {
            const recoverySettings = RecoverySettingsModel.getRecoverySettings();
            const { ob_decline_recovery = 0, retry_with_apm = 0, option_id: ob_option_id } = recoverySettings;
            const first_gateway_details = isPanoramicTheme;

            dispatch(fetchGateways(ob_decline_recovery, retry_with_apm, first_gateway_details)).then(({ data = {} } = {}) => {
                if (data.status !== ResponseStatus.OK) return;
                const { gateways = [] } = data;

                if (gateways.length > 0 && gateways[0]?.gateway_details) {
                    const firstGatewayDetails = gateways[0].gateway_details;
                    const { psp_bank_id, card_type_name = null } = firstGatewayDetails;
                    let updatedAdditionalFields;

                    if (psp_bank_id) {
                        updatedAdditionalFields = updateAdditionalFields(firstGatewayDetails?.additional_fields, psp_bank_id);
                    }

                    dispatch(updateGatewayDetails({
                        ...firstGatewayDetails,
                        card_type_name,
                        ...(updatedAdditionalFields && {
                            additional_fields: updatedAdditionalFields
                        })
                    }))
                    dispatch(setLimitSubmit(false));
                    history.push("payment-method");
                    return data.gateway_details;
                }

                if (ob_decline_recovery || retry_with_apm) {
                    const preSelectedGateway = gateways.find(gateway => gateway.option_id === ob_option_id)
                    return createGateway(preSelectedGateway || gateways[0])
                }

                if (needForAutoCreate(gateways)) {
                    return createGateway(gateways[0])
                }
            })
        } else if (needForAutoCreate(gatewaysList?.gateways)) {
            createGateway(gatewaysList?.gateways[0])
        }
    }, [location, transaction_type])

    useEffect(() => {
        const isCallback = defineIsCallback();
        const callback = isCallback ? 1 : 0;
        dispatch(getCustomerData(callback))
            .then((data) => {
                processingData(data).then(() => setLoader(false)).catch(() => setLoader(false))
            })
    }, [getCustomerData, location]);

    useEffect(() => {
        const useDataLoaded = base_amount !== undefined && base_currency !== undefined && customer !== undefined && order_id !== undefined;
        if (!gatewaysList || !useDataLoaded) return;

        cashierLoaded({
            cid: customer.pin,
            amount: base_amount,
            currency: base_currency,
            order_id: order_id,
            payment_methods: gatewaysList.gateways.map(gateway => gateway.payment_method_key),
            message: "Cashier loaded successfully"
        })
    }, [gatewaysList, base_amount, base_currency, customer, order_id]);

    const handleLoadingOfCustomCss = () => setCustomCssLoaded(true);

    useEffect(() => {
        document.body.addEventListener('NEW_STYLES_ADDED', handleLoadingOfCustomCss);

        return () => {
            document.body.removeEventListener('NEW_STYLES_ADDED', handleLoadingOfCustomCss);
        };
    }, [])

    const defineIsCallback = () => {
        try {
            const url = new URL(location?.href);
            const queryKeys = Array.from(url?.searchParams?.keys());
            return queryKeys?.includes('callback');
        } catch (err) {
            const errorText = "Cant parse url";

            sendOpenSearchLogs({
                event_group: "processing_customer_data",
                error_data: {
                    error: {
                        message: errorText,
                        stack: err
                    }
                }
            });

            console.error(errorText, err);
            return false;
        }
    }

    const processingData = async (profile = {}) => {
        const { customer: { country, balance } = {}, base_amount, frontend_name: name, session_type, transaction_type: type } = profile;

        if (session_type === 'vt') {
            document.title = `Terminal | ${name ? name : ""}`;
        } else if (session_type === 'cashier') {
            document.title = `${config.title} | ${name ? name : ""}`;
        }

        if (type === "pay-out" && base_amount && balance && +base_amount > +balance) {
            dispatch(createFatalError("Please try again with lower amount."))
        }

        if (country) {
            document.documentElement.setAttribute("data-country", country);
        }

    };

    if (loader) return <>
        {customCss && customCssLoaded ? <Loader /> : <Loader text={langBack('initialLoadingText', false, true)} showLoaderIcon={false} />}
    </>;

    return children
};

export default compose(
    withI18N, withTemplate
)(ProcessingCustomerData)
